import cookie from 'js-cookie';
import { TOKEN, REFRESH_TOKEN } from './constants/config';
import { actionRefreshToken } from '../view/system/systemAction';

/*
 * One token refresh at a time, across every window of this browser.
 *
 * Keycloak makes refresh tokens single-use (INSA penetration test, finding 3:
 * a copied refresh token could be replayed).  Presenting a refresh token that
 * was already used is treated as theft and ENDS THE LOGIN.  Every dashboard
 * tab and viewer window shares the same refresh_token cookie, and they all
 * see the access token expire at the same moment -- so without coordination
 * two of them would refresh with the same token and log the user out.
 *
 * So every refresh runs under one browser-wide lock (Web Locks API, shared by
 * all same-origin windows), always uses the refresh token CURRENTLY in the
 * cookie, and writes the new pair before releasing the lock.  A window that
 * waited for the lock and finds the pair already replaced by another window
 * uses that instead of refreshing again.
 *
 * The viewer's app-config.js takes the same lock (same name) -- change both
 * together.
 */

const LOCK_NAME = 'afyanx-token-refresh';
// An access token with less than this left is not worth reusing.
const MIN_REMAINING_S = 30;
// Fallback for browsers without Web Locks: a short-lived marker in
// localStorage.  Narrower protection, same behaviour otherwise.
const FALLBACK_KEY = 'afyanx.refreshingUntil';
const FALLBACK_HOLD_MS = 10 * 1000;

const secondsLeft = (token) => {
  try {
    let part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    while (part.length % 4) part += '=';
    const exp = JSON.parse(atob(part)).exp;
    return typeof exp === 'number' ? exp - Date.now() / 1000 : -1;
  } catch (e) {
    return -1;
  }
};

const storePair = (data) => {
  cookie.set(TOKEN, data.access_token, {
    expires: new Date((data.expires_in || 1800) * 1000 + Date.now()),
  });
  cookie.set(REFRESH_TOKEN, data.refresh_token, {
    expires: new Date((data.refresh_expires_in || 1800) * 1000 + Date.now()),
  });
};

// Runs inside the lock.  `seen` is the refresh token this window held when it
// decided to refresh.
const refreshUnderLock = async (seen) => {
  const current = cookie.get(REFRESH_TOKEN);
  const access = cookie.get(TOKEN);
  if (current && current !== seen && access && secondsLeft(access) > MIN_REMAINING_S) {
    return access; // another window refreshed while this one waited
  }
  if (!current) throw new Error('no refresh token');
  const res = await actionRefreshToken(current);
  if (!res?.data?.access_token) throw new Error('refresh returned no token');
  storePair(res.data);
  return res.data.access_token;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withFallbackLock = async (fn) => {
  let until = 0;
  try {
    until = Number(localStorage.getItem(FALLBACK_KEY)) || 0;
  } catch (e) {
    // storage blocked: no cross-window protection
  }
  if (until > Date.now()) await sleep(Math.min(until - Date.now(), FALLBACK_HOLD_MS) + 250);
  try {
    localStorage.setItem(FALLBACK_KEY, String(Date.now() + FALLBACK_HOLD_MS));
  } catch (e) {
    // ignore
  }
  try {
    return await fn();
  } finally {
    try {
      localStorage.removeItem(FALLBACK_KEY);
    } catch (e) {
      // ignore
    }
  }
};

let inFlight = null;

/** Refresh the token pair (or adopt one another window just obtained).
 *  Resolves to the access token; rejects when the login can't be refreshed. */
export const refreshTokens = () => {
  if (inFlight) return inFlight;
  const seen = cookie.get(REFRESH_TOKEN);
  const run = () => refreshUnderLock(seen);
  const locked =
    typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request
      ? navigator.locks.request(LOCK_NAME, run)
      : withFallbackLock(run);
  inFlight = locked.finally(() => {
    inFlight = null;
  });
  return inFlight;
};
